import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ROLES, CLIENTS, getUserByEmail } from '../data/mockData';
import { haptic } from '../native';

export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';
const CAN_SWITCH_VIEW = [ROLES.ADMIN, ROLES.TEAM_LEAD, 'admin', 'team_lead'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mca_user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('mca_token') || null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('mca_user'));
      if (!u) return null;
      return (u.role === ROLES.CLIENT || u.role === 'client') ? 'client' : 'rep';
    } catch { return null; }
  });

  const canSwitchView = user && CAN_SWITCH_VIEW.includes(user.role);

  const login = async (email, password, utmCampaign = '') => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, utm_campaign: utmCampaign || undefined }),
    });
    if (res.ok) {
      const data = await res.json();
      const sessionUser = {
        id: data.user.id,
        name: data.user.full_name || data.user.name,
        email: data.user.email,
        role: data.user.role,
        repId: data.user.rep_id || null,
        clientId: data.user.client_id || null,
        token: data.token,
      };
      localStorage.setItem('mca_user', JSON.stringify(sessionUser));
      localStorage.setItem('mca_token', data.token);
      setUser(sessionUser);
      setToken(data.token);
      setViewMode((sessionUser.role === 'client') ? 'client' : 'rep');
      haptic('success');
      return sessionUser;
    }
    haptic('error');
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.message || 'Invalid email or password');
  };

  const logout = () => {
    localStorage.removeItem('mca_user');
    localStorage.removeItem('mca_token');
    setUser(null);
    setToken(null);
    setViewMode(null);
  };

  const switchView = (mode) => { if (canSwitchView) setViewMode(mode); };

  const can = {
    seeAllReps: ['admin', 'team_lead'].includes(user?.role),
    seeAllClients: ['admin', 'team_lead'].includes(user?.role),
    seeClient: (clientId) => {
      if (!user) return false;
      if (user.role === ROLES.ADMIN || user.role === 'admin') return true;
      if (user.role === ROLES.REP || user.role === 'rep') {
        return CLIENTS.some(c => c.id === clientId && c.assignedRepId === user.repId);
      }
      return user.clientId === clientId;
    },
    seeInternalDocs: !['client'].includes(user?.role),
    // Everyone in SUPER_ADMIN_EMAILS already holds the 'admin' role (see
    // client/src/data/superAdmins.js) - there is no separate role value for
    // "super admin", so gating on role alone already covers both.
    seeCreditPanel: user?.role === 'admin',
    seeSeasonalityUnderwriting: false,
    uploadForClient: !['client'].includes(user?.role),
    requestDocs: !['client'].includes(user?.role),
    managePermissions: user?.role === 'admin',
    reassignClients: ['admin', 'team_lead'].includes(user?.role),
    manageUsers: user?.role === 'admin',
  };

  const sessionStart = useRef(Date.now());
  useEffect(() => {
    if (!user || !token) return;
    sessionStart.current = Date.now();
    const sendSession = () => {
      const dur = Math.round((Date.now() - sessionStart.current) / 1000);
      if (dur < 2) return;
      try { navigator.sendBeacon(`${API}/client-data/session`, new Blob([JSON.stringify({ durationSec: dur })], { type: 'application/json' })); } catch {}
    };
    window.addEventListener('beforeunload', sendSession);
    return () => window.removeEventListener('beforeunload', sendSession);
  }, [user, token]);

  return (
    <AuthContext.Provider value={{ user, token, viewMode, canSwitchView, login, logout, switchView, can }}>
      {children}
    </AuthContext.Provider>
  );
}
